import { Bell, Armchair, X } from 'lucide-react';
import type { Session } from '@/types';

interface SessionCardProps {
  session: Session;
  onNotify: () => void;
  onSeat: () => void;
  onCancel: () => void;
}

function statusColor(status: Session['status']): string {
  switch (status) {
    case 'waiting':
      return 'var(--qr-primary)';
    case 'notified':
      return 'var(--qr-success)';
    case 'seated':
      return 'var(--qr-text-secondary)';
    default:
      return 'var(--qr-border)';
  }
}

function statusLabel(status: Session['status']): string {
  const map: Record<string, string> = {
    waiting: 'En espera',
    notified: 'Notificado',
    seated: 'Sentado',
    served: 'Servido',
    cancelled: 'Cancelado',
    no_show: 'No show',
  };
  return map[status] || status;
}

function timeSince(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

export function SessionCard({ session, onNotify, onSeat, onCancel }: SessionCardProps) {
  const canNotify = session.status === 'waiting';
  const canSeat = session.status === 'waiting' || session.status === 'notified';
  const canCancel = session.status === 'waiting' || session.status === 'notified';

  return (
    <div
      className="flex flex-col gap-3 p-4 rounded-xl animate-fade-in transition-transform duration-200 active:scale-[0.99]"
      style={{
        background: 'var(--qr-surface)',
        borderLeft: `4px solid ${statusColor(session.status)}`,
        borderTop: '1px solid var(--qr-border)',
        borderRight: '1px solid var(--qr-border)',
        borderBottom: '1px solid var(--qr-border)',
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-bold" style={{ color: 'var(--qr-text)' }}>
            {session.client_name}
          </h3>
          <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--qr-text-secondary)' }}>
            <span className="flex items-center gap-1">
              <Armchair size={14} />
              {session.party_size}
            </span>
            <span>{timeSince(session.arrived_at)}</span>
            {typeof session.position === 'number' && session.status === 'waiting' && (
              <span className="font-semibold" style={{ color: 'var(--qr-primary)' }}>
                Pos #{session.position + 1}
              </span>
            )}
          </div>
        </div>
        <span
          className="text-xs font-semibold px-2 py-1 rounded-md uppercase tracking-wide"
          style={{
            background: statusColor(session.status) + '22',
            color: statusColor(session.status),
          }}
        >
          {statusLabel(session.status)}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mt-1">
        {canNotify && (
          <button
            onClick={onNotify}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-transform duration-200 active:scale-[0.96]"
            style={{ background: 'var(--qr-primary)', color: '#fff' }}
          >
            <Bell size={14} />
            Notificar
          </button>
        )}
        {canSeat && (
          <button
            onClick={onSeat}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-transform duration-200 active:scale-[0.96]"
            style={{ background: 'var(--qr-success)', color: '#fff' }}
          >
            <Armchair size={14} />
            Mesa
          </button>
        )}
        {canCancel && (
          <button
            onClick={onCancel}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-transform duration-200 active:scale-[0.96]"
            style={{
              background: 'transparent',
              border: '1px solid var(--qr-danger)',
              color: 'var(--qr-danger)',
            }}
          >
            <X size={14} />
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}
